import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { cat_id2lbl } from '../../lib/cat';
import BrandHeader from '../../components/brand/BrandHeader';

import xdate from '../../../../../util/date.js';

type Campaign = {
  id: string;
  name: string;
  description: string;
  category: string;
  landing_page?: string;
  campaign_assets?: Array<{
    id: string;
    name: string;
    asset_type: string;
    file_url: string;
    mime_type: string;
  }>;
  budget_info?: {
    cpm: number;
    total_budget: number;
    spent: number;
    remaining: number;
  };
  targeting?: {
    countries?: string[];
    age_ranges?: number[][];
    genders?: string[];
    content_categories?: string[];
  };
  allowed_ad_formats?: string[];
  sched?: {
    is_always_on?: boolean;
    start_date?: string;
    end_date?: string;
  };
  is_active?: boolean;
  status?: string;
  ts?: {insert: Date, modified: Date};
  active_creators_count?: number;
};

type VideoCampaign = {
  id: string;
  video_id: string;
  processing_status: string;
  processed_video_url?: string;
  ts?: {insert: Date, modified: Date};
  videos?: {
    id: string;
    title: string;
    video_url: string;
    thumbnail_url?: string;
    duration: number;
    creator_id: string;
  };
  creator_profile?: {
    id: string;
    full_name: string;
    profile_image_url?: string;
  };
};

function AssetPreview({ asset }: { asset: any }) {
  const [showPreview, setShowPreview] = useState(false);
  const isVideo = asset?.mime_type?.startsWith('video/');

  return (
    <>
      <div
        onClick={() => setShowPreview(true)}
        className="relative bg-gradient-to-br from-gray-900 to-gray-700 rounded-lg overflow-hidden cursor-pointer group"
        style={{ aspectRatio: '16/9' }}
      >
        {isVideo ? (
          <video
            src={asset.file_url}
            className="w-full h-full object-contain"
            muted
          />
        ) : (
          <img
            src={asset.file_url}
            alt={asset.name}
            className="w-full h-full object-contain"
          />
        )}

        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
          <div className="px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold">
            View Full
          </div>
        </div>

        {}
        <div className="absolute top-2 right-2">
          <span className="px-2 py-1 bg-blue-500 text-white text-xs font-semibold rounded">
            {asset.asset_type === 'video_ad' ? '📺' : '🖼️'}
          </span>
        </div>
      </div>

      <p className="text-sm font-semibold text-gray-900 mt-2 truncate">{asset.name}</p>

      {}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl">
            <button
              onClick={() => setShowPreview(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300"
            >
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
              </svg>
            </button>
            <div className="bg-black rounded-xl overflow-hidden">
              {isVideo ? (
                <video src={asset.file_url} controls autoPlay className="w-full" />
              ) : (
                <img src={asset.file_url} alt={asset.name} className="w-full" />
              )}
            </div>
            <div className="mt-4 text-white text-center">
              <h3 className="text-xl font-bold">{asset.name}</h3>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CreatorVideoCard({ videoCampaign, onClick }:
  { videoCampaign: VideoCampaign, onClick?: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  
  const videoUrl = videoCampaign.processed_video_url || videoCampaign.videos?.video_url;
  const isProcessed = !!videoCampaign.processed_video_url;

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-lg transition-all ${
        onClick ? 'cursor-pointer' : ''
      }`}
    >
      {}
      <div
        className="relative bg-gradient-to-br from-gray-900 to-gray-700 overflow-hidden group cursor-pointer"
        style={{ aspectRatio: '9/16' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {videoUrl ? (
          <>
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full object-cover"
              muted
              loop
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
              <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                </svg>
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white text-4xl">
            🎬
          </div>
        )}

        {}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
            videoCampaign.processing_status === 'completed' ? 'bg-green-500 text-white' :
            videoCampaign.processing_status === 'processing' ? 'bg-yellow-500 text-white' :
            videoCampaign.processing_status === 'analyzing' ? 'bg-blue-500 text-white' :
            'bg-gray-500 text-white'
          }`}>
            {videoCampaign.processing_status === 'completed' ? '✓ With Ad' :
             videoCampaign.processing_status === 'processing' ? '⏳ Processing' :
             videoCampaign.processing_status === 'analyzing' ? '🤖 Analyzing' :
             videoCampaign.processing_status}
          </span>
        </div>

        {}
        {isProcessed && (
          <div className="absolute top-2 left-2">
            <div className="px-2 py-1 bg-blue-500 text-white rounded text-xs font-semibold">
              📺 Processed
            </div>
          </div>
        )}

        {}
        {videoCampaign.videos?.duration && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 px-2 py-1 rounded text-white text-xs font-semibold">
            {Math.floor(videoCampaign.videos.duration / 60)}:{String(Math.floor(videoCampaign.videos.duration % 60)).padStart(2, '0')}
          </div>
        )}
      </div>

      {}
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          {videoCampaign.creator_profile?.profile_image_url ? (
            <img
              src={videoCampaign.creator_profile.profile_image_url}
              alt={videoCampaign.creator_profile.full_name}
              className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
              {videoCampaign.creator_profile?.full_name?.[0] || 'C'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-gray-900 truncate">
              {videoCampaign.creator_profile?.full_name || 'Creator'}
            </p>
          </div>
        </div>

        <h4 className="font-semibold text-sm text-gray-900 mb-1 line-clamp-2">
          {videoCampaign.videos?.title || 'Untitled Video'}
        </h4>

        <p className="text-xs text-gray-500">
          {videoCampaign.ts?.insert ? xdate.fmt_lbl_short(videoCampaign.ts.insert) : ''}
        </p>
      </div>
    </div>
  );
}

export default function CampaignDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [videoCampaigns, setVideoCampaigns] = useState<VideoCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampaignData();
  }, [id]);

  const fetchCampaignData = async () => {
    try {
      const campaignResult = await back_app.campaign_get(id);

      if (!campaignResult.err && campaignResult) {
        setCampaign(campaignResult);

        const vcResult = await back_app.vcamp_list({campaign_id: id});

        if (!vcResult.err && Array.isArray(vcResult)) {
          
          setVideoCampaigns(vcResult);
        }
      } else {
        navigate('/brand/campaigns');
      }
    } catch (error) {
      console.error('Error fetching campaign:', error);
      if ((error as Error)?.message == 'no session') navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📋</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Campaign not found</h2>
          <button
            onClick={() => navigate('/brand/campaigns')}
            className="text-blue-600 hover:underline font-semibold"
          >
            Back to campaigns
          </button>
        </div>
      </div>
    );
  }

  const spent = campaign.budget_info?.spent || 0;
  const totalBudget = campaign.budget_info?.total_budget || 0;
  const remaining = campaign.budget_info?.remaining || 0;
  const spentPercentage = totalBudget > 0 ? (spent / totalBudget) * 100 : 0;
  const estimatedImpressions = campaign.budget_info?.cpm
    ? Math.floor((totalBudget / campaign.budget_info.cpm) * 1000)
    : 0;

  const completedVideos = videoCampaigns.filter(vc => vc.processing_status === 'completed').length;
  const processingVideos = videoCampaigns.filter(vc => 
    vc.processing_status === 'processing' || vc.processing_status === 'analyzing'
  ).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandHeader />

      {}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button
            onClick={() => navigate('/brand/campaigns')}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-4 transition-colors"
          >
            <span>←</span>
            <span>Back to campaigns</span>
          </button>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">{campaign.name}</h2>
              {campaign.description && (
                <p className="text-white/90 mb-3">{campaign.description}</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {campaign.category && (
                  <span className="px-3 py-1 bg-white/20 backdrop-blur text-white rounded-full text-sm font-semibold">
                    {cat_id2lbl(campaign.category)}
                  </span>
                )}
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  campaign.status == 'active'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-400 text-white'
                }`}>
                  {campaign.status == 'active' ? '✓ Active' : '⏸ Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-sm text-gray-600 mb-1">Total Budget</div>
            <div className="text-2xl font-bold text-gray-900">
              ${totalBudget.toLocaleString()}
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-sm text-gray-600 mb-1">Active Creators</div>
            <div className="text-2xl font-bold text-purple-600">
              {videoCampaigns.length}
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-sm text-gray-600 mb-1">Completed</div>
            <div className="text-2xl font-bold text-green-600">
              {completedVideos}
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-sm text-gray-600 mb-1">Processing</div>
            <div className="text-2xl font-bold text-yellow-600">
              {processingVideos}
            </div>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {}
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Budget Overview</h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">CPM</span>
                  <span className="font-bold text-blue-600">${campaign.budget_info?.cpm || 0}</span>
                </div>
              </div>

              {estimatedImpressions > 0 && (
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Est. Impressions</span>
                    <span className="font-bold text-gray-900">
                      {estimatedImpressions.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Spent</span>
                  <span className="font-bold text-red-600">${spent.toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all"
                    style={{ width: `${Math.min(spentPercentage, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Remaining</span>
                  <span className="font-bold text-green-600">${remaining.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Targeting</h3>
            
            <div className="space-y-3 text-sm">
              {campaign.targeting?.countries && campaign.targeting.countries.length > 0 ? (
                <div>
                  <div className="font-semibold text-gray-700 mb-1">Countries</div>
                  <div className="flex flex-wrap gap-1">
                    {campaign.targeting.countries.slice(0, 3).map((country) => (
                      <span key={country} className="px-2 py-1 bg-gray-100 rounded text-xs">
                        {country}
                      </span>
                    ))}
                    {campaign.targeting.countries.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                        +{campaign.targeting.countries.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-semibold text-gray-700 mb-1">Countries</div>
                  <div className="text-gray-500">All countries</div>
                </div>
              )}

              {campaign.targeting?.age_ranges && campaign.targeting.age_ranges.length > 0 ? (
                <div>
                  <div className="font-semibold text-gray-700 mb-1">Age Ranges</div>
                  <div className="flex flex-wrap gap-1">
                    {campaign.targeting.age_ranges.map((range, idx) => (
                      <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                        {range[0]}-{range[1] === 100 ? '65+' : range[1]}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-semibold text-gray-700 mb-1">Age Ranges</div>
                  <div className="text-gray-500">All ages</div>
                </div>
              )}

              {campaign.targeting?.genders && campaign.targeting.genders.length > 0 ? (
                <div>
                  <div className="font-semibold text-gray-700 mb-1">Gender</div>
                  <div className="flex flex-wrap gap-1">
                    {campaign.targeting.genders.map((gender) => (
                      <span key={gender} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs capitalize">
                        {gender === 'any' ? 'Any' : gender}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-semibold text-gray-700 mb-1">Gender</div>
                  <div className="text-gray-500">All genders</div>
                </div>
              )}

              {campaign.targeting?.content_categories && campaign.targeting.content_categories.length > 0 && (
                <div>
                  <div className="font-semibold text-gray-700 mb-1">Content</div>
                  <div className="flex flex-wrap gap-1">
                    {campaign.targeting.content_categories.slice(0, 3).map((cat) => (
                      <span key={cat} className="px-2 py-1 bg-gray-100 rounded text-xs">
                        {cat_id2lbl(cat)}
                      </span>
                    ))}
                    {campaign.targeting.content_categories.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                        +{campaign.targeting.content_categories.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {}
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Details</h3>
            
            <div className="space-y-3 text-sm">
              <div>
                <div className="font-semibold text-gray-700 mb-1">Schedule</div>
                {campaign.sched?.is_always_on ? (
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                    Always Active
                  </span>
                ) : (
                  <div className="text-gray-600">
                    {campaign.sched?.start_date && (
                      <div>Start: {xdate.fmt_lbl_short(campaign.sched.start_date)}</div>
                    )}
                    {campaign.sched?.end_date && (
                      <div>End: {xdate.fmt_lbl_short(campaign.sched.end_date)}</div>
                    )}
                  </div>
                )}
              </div>

              {campaign.landing_page && (
                <div>
                  <div className="font-semibold text-gray-700 mb-1">Landing Page</div>
                  <a
                    href={campaign.landing_page}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {campaign.landing_page}
                  </a>
                </div>
              )}

              <div>
                <div className="font-semibold text-gray-700 mb-1">Created</div>
                <div className="text-gray-600">
                  {campaign.ts?.insert ? xdate.fmt_lbl_short(campaign.ts.insert) : ''}
                </div>
              </div>

              {campaign.allowed_ad_formats && campaign.allowed_ad_formats.length > 0 && (
                <div>
                  <div className="font-semibold text-gray-700 mb-1">Ad Format</div>
                  <div className="flex flex-wrap gap-1">
                    {campaign.allowed_ad_formats.map((format) => (
                      <span key={format} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        {format === 'break' ? 'Video Break' : 'Overlay'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {}
        {campaign.campaign_assets && campaign.campaign_assets.length > 0 && (
          <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Campaign Assets
                <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                  {campaign.campaign_assets.length}
                </span>
              </h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {campaign.campaign_assets.map((asset) => (
                <div key={asset.id}>
                  <AssetPreview asset={asset} />
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Creators can choose from these {campaign.campaign_assets.length} {campaign.campaign_assets.length === 1 ? 'asset' : 'assets'} for their videos
              </p>
            </div>
          </div>
        )}

        {}
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Connected Creators</h3>
              <p className="text-sm text-gray-600 mt-1">
                {videoCampaigns.length} {videoCampaigns.length === 1 ? 'creator' : 'creators'} participating • {completedVideos} completed with ads
              </p>
            </div>
          </div>

          {videoCampaigns.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👥</div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">No creators yet</h4>
              <p className="text-gray-600">
                Creators will appear here as they join your campaign
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {videoCampaigns.map((vc) => (
                <CreatorVideoCard
                  key={vc.id}
                  videoCampaign={vc}
                  onClick={() => navigate(
                    `/brand/campaigns/${id}/creator/${vc.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}